import type { CompRef } from '@wix/document-services-types';
import { WidgetViewState } from '@wix/editor-platform-sdk-types';
import { ManifestComponentExtensionType } from '../stores';
import { AbstractExternalAPIHandler } from './AbstractExternalAPIHandler';
import { ChangeType } from '../ManifestChangeDetection';
import { CacheMethodResponse } from '../api/decorators/CacheMethodResponse';
import { MethodCacheStrategy } from '../api/MethodCacheStrategy';
import { DependencyToken } from '../dependencies';
import type { EditorPointer } from '@wix/editor-document-logic-api';

interface IStateEntry {
  state: string;
  editorPointer: EditorPointer<CompRef>;
  type: ManifestComponentExtensionType;
}
const GLOBAL_STATE_MAP = new Map<string, IStateEntry>();

export class ExternalStateHandler extends AbstractExternalAPIHandler {
  private getEntryKey(editorPointer: EditorPointer<CompRef>) {
    const documentPointer =
      this.editorPointerAPI.getDocumentPointer(editorPointer);
    const innerElementPath =
      this.editorPointerAPI.getInnerElementPath(editorPointer);

    return this.editorPointerAPI.getEditorPointerUniqueId(
      documentPointer,
      innerElementPath,
      { ignoreVariants: true },
    );
  }

  getViewState(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
  ) {
    if (type !== ManifestComponentExtensionType.OOI) {
      return null;
    }

    const componentRef =
      this.editorPointerAPI.getDocumentPointer(editorPointer);
    const getViewState = this.dependenciesContributor.get(
      DependencyToken.VIEW_STATE_GET,
    );
    return getViewState(componentRef) || null;
  }

  setViewState(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    state: string,
  ) {
    if (type !== ManifestComponentExtensionType.OOI) {
      return;
    }

    const componentRef =
      this.editorPointerAPI.getDocumentPointer(editorPointer);
    const setViewState = this.dependenciesContributor.get(
      DependencyToken.VIEW_STATE_UPDATE,
    );
    setViewState(componentRef, state);
    GLOBAL_STATE_MAP.set(this.getEntryKey(editorPointer), {
      state,
      editorPointer,
      type,
    });
  }

  /**
   * cache reason – OOI state getter
   * works with compRef, so it ignores innerElementPath
   * means that we can share any OOI states call for ROOT and all children
   */
  @CacheMethodResponse<
    [EditorPointer<CompRef>, ManifestComponentExtensionType]
  >({
    cacheStrategy: MethodCacheStrategy.Permanent,
    buildCacheKey: (_self, editorPointer, extensionType) => {
      if (extensionType === ManifestComponentExtensionType.OOI) {
        const documentPointer =
          _self.editorPointerAPI!.getDocumentPointer(editorPointer);
        return _self.editorPointerAPI!.getEditorPointerUniqueId(
          documentPointer,
          undefined,
          { ignoreVariants: true },
        );
      }

      return null;
    },
    extractEditorPointers: (_self, editorPointer) => [editorPointer],
  })
  getViewStates(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
  ) {
    if (type !== ManifestComponentExtensionType.OOI) {
      return {};
    }

    const componentRef =
      this.editorPointerAPI.getDocumentPointer(editorPointer);
    const getStageData = this.dependenciesContributor.get(
      DependencyToken.DM_STAGE_DATA_GET,
    );
    const stageData = getStageData(componentRef);
    const viewStates = (stageData?.viewStates ?? {}) as Record<
      string,
      WidgetViewState
    >;

    const filteredViewStates = Object.fromEntries(
      Object.entries(viewStates).filter(([_, value]) => !value?.hidden),
    );

    if (Object.keys(filteredViewStates).length === 0) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(filteredViewStates).map(([key, state]) => [
        key,
        {
          displayName: state.title,
          initial: state.initial,
        },
      ]),
    );
  }

  private getInitialViewState(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
  ) {
    const viewStates = this.getViewStates(editorPointer, type);
    if (!Object.keys(viewStates).length) {
      return undefined;
    }

    const initialState = Object.entries(viewStates).find(
      ([_stateId, viewState]) => {
        return viewState.initial;
      },
    );

    if (!initialState) {
      return undefined;
    }

    const [id, viewState] = initialState;

    return {
      id,
      viewState,
    };
  }

  resetViewStates(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
  ) {
    if (type !== ManifestComponentExtensionType.OOI) {
      return;
    }

    const initialViewState = this.getInitialViewState(editorPointer, type)?.id;
    if (!initialViewState) {
      return;
    }

    GLOBAL_STATE_MAP.delete(this.getEntryKey(editorPointer));
    this.setViewState(editorPointer, type, initialViewState);
  }

  getCurrentState(
    editorPointer: EditorPointer<CompRef>,
    _type: ManifestComponentExtensionType,
  ) {
    return GLOBAL_STATE_MAP.get(this.getEntryKey(editorPointer))?.state || null;
  }

  setCurrentState(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    state: string,
  ) {
    GLOBAL_STATE_MAP.set(this.getEntryKey(editorPointer), {
      state,
      editorPointer,
      type,
    });

    this.manifestChangeDetection.notify({
      type: ChangeType.State,
      editorPointer,
      extensionType: type,
      value: state,
    });
  }

  resetCurrentStates(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
  ) {
    GLOBAL_STATE_MAP.delete(this.getEntryKey(editorPointer));
    this.manifestChangeDetection.notify({
      type: ChangeType.State,
      editorPointer,
      extensionType: type,
      value: null,
    });
  }

  getGlobalStates() {
    return Array.from(GLOBAL_STATE_MAP.values());
  }

  getDesignStates(editorPointers: EditorPointer<CompRef>[]): string[] {
    const fn = this.dependenciesContributor.get(
      DependencyToken.DESIGN_STATES_GET,
    );
    return fn(editorPointers);
  }

  setDesignStates(
    editorPointers: EditorPointer<CompRef>[],
    states: string[],
  ): void {
    const fn = this.dependenciesContributor.get(
      DependencyToken.DESIGN_STATES_SET,
    );
    fn(editorPointers, states);
    editorPointers.forEach((editorPointer) => {
      this.manifestChangeDetection.notify({
        type: ChangeType.DesignState,
        editorPointer,
        extensionType: ManifestComponentExtensionType.BUILDER,
        value: states,
      });
    });
  }

  resetDesignStates(editorPointers: EditorPointer<CompRef>[]): void {
    const fn = this.dependenciesContributor.get(
      DependencyToken.DESIGN_STATES_RESET,
    );
    fn(editorPointers);
    editorPointers.forEach((editorPointer) => {
      this.manifestChangeDetection.notify({
        type: ChangeType.DesignState,
        editorPointer,
        extensionType: ManifestComponentExtensionType.BUILDER,
        value: [],
      });
    });
  }
}
